import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload,
  Download,
  CheckCircle,
  Error as ErrorIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useRef, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError as parseErrorUtil } from "../../../utils";

interface InboxImportDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  companyId: string;
  eventListener: (event: any) => void;
  onImportComplete: () => void;
}

interface ParsedInbox {
  email: string;
  firstName: string;
  lastName: string;
  imapUsername: string;
  imapPassword: string;
  imapHost: string;
  imapPort: string;
  smtpUsername: string;
  smtpPassword: string;
  smtpHost: string;
  smtpPort: string;
  dailyLimit: string;
  warmupEnabled: string;
  warmupLimit: string;
  warmupIncrement: string;
}

interface ImportResult {
  email: string;
  success: boolean;
  error?: string;
}

const TEMPLATE_HEADERS = [
  "Email",
  "First Name",
  "Last Name",
  "IMAP Username",
  "IMAP Password",
  "IMAP Host",
  "IMAP Port",
  "SMTP Username",
  "SMTP Password",
  "SMTP Host",
  "SMTP Port",
  "Daily Limit",
  "Warmup Enabled",
  "Warmup Limit",
  "Warmup Increment",
];

const TEMPLATE_EXAMPLE = [
  "john@example.com",
  "John",
  "Doe",
  "john@example.com",
  "password123",
  "imap.example.com",
  "993",
  "john@example.com",
  "password123",
  "smtp.example.com",
  "587",
  "20",
  "true",
  "5",
  "1",
];

const InboxImportDialog: React.FC<InboxImportDialogProps> = ({
  open,
  onClose,
  getToken,
  companyId,
  eventListener,
  onImportComplete,
}) => {
  const [loading, setLoading] = useState(false);
  const [parsedInboxes, setParsedInboxes] = useState<ParsedInbox[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [importComplete, setImportComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setParsedInboxes([]);
    setParseError(null);
    setImportResults([]);
    setImportComplete(false);
    onClose();
  };

  const downloadTemplate = () => {
    const csvContent = [
      TEMPLATE_HEADERS.join(","),
      TEMPLATE_EXAMPLE.map((val) => `"${val}"`).join(","),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "inbox_import_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCSV = (content: string): ParsedInbox[] => {
    const lines = content.split("\n").filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error("CSV must have a header row and at least one data row");
    }

    // Parse header row
    const headers = parseCSVLine(lines[0]);

    // Validate headers
    const requiredHeaders = ["Email", "IMAP Username", "IMAP Password", "IMAP Host", "SMTP Host"];
    const missingHeaders = requiredHeaders.filter(
      (h) => !headers.some((header) => header.toLowerCase() === h.toLowerCase())
    );
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(", ")}`);
    }

    // Create header index map (case-insensitive)
    const headerIndex: { [key: string]: number } = {};
    headers.forEach((h, i) => {
      headerIndex[h.toLowerCase().trim()] = i;
    });

    // Parse data rows
    const inboxes: ParsedInbox[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === 0 || values.every((v) => !v.trim())) continue;

      const getValue = (header: string): string => {
        const idx = headerIndex[header.toLowerCase()];
        return idx !== undefined ? values[idx]?.trim() || "" : "";
      };

      inboxes.push({
        email: getValue("email"),
        firstName: getValue("first name"),
        lastName: getValue("last name"),
        imapUsername: getValue("imap username"),
        imapPassword: getValue("imap password"),
        imapHost: getValue("imap host"),
        imapPort: getValue("imap port") || "993",
        smtpUsername: getValue("smtp username"),
        smtpPassword: getValue("smtp password"),
        smtpHost: getValue("smtp host"),
        smtpPort: getValue("smtp port") || "587",
        dailyLimit: getValue("daily limit") || "20",
        warmupEnabled: getValue("warmup enabled") || "false",
        warmupLimit: getValue("warmup limit") || "5",
        warmupIncrement: getValue("warmup increment") || "1",
      });
    }

    return inboxes;
  };

  // Parse a single CSV line, handling quoted values
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());

    return result;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const inboxes = parseCSV(content);
        if (inboxes.length === 0) {
          setParseError("No valid inbox data found in CSV");
          return;
        }
        setParsedInboxes(inboxes);
        setParseError(null);
        setImportResults([]);
        setImportComplete(false);
      } catch (err: any) {
        setParseError(err.message || "Failed to parse CSV file");
        setParsedInboxes([]);
      }
    };
    reader.onerror = () => {
      setParseError("Failed to read file");
    };
    reader.readAsText(file);

    // Reset file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImport = async () => {
    if (parsedInboxes.length === 0) return;

    setLoading(true);
    setImportResults([]);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/inboxes/bulk-import`,
        {
          companyId,
          inboxes: parsedInboxes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setImportResults(response.data.results);
      setImportComplete(true);

      const successCount = response.data.results.filter(
        (r: ImportResult) => r.success
      ).length;
      const failCount = response.data.results.filter(
        (r: ImportResult) => !r.success
      ).length;

      if (successCount > 0) {
        eventListener({
          type: "SET_SUCCESS",
          message: `Successfully imported ${successCount} inbox(es)${failCount > 0 ? `, ${failCount} failed` : ""}`,
        });
        onImportComplete();
      } else if (failCount > 0) {
        eventListener({
          type: "SET_ERROR",
          error: `Failed to import ${failCount} inbox(es). Check the results below.`,
        });
      }
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseErrorUtil(err),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Import Inboxes from CSV
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Import multiple IMAP inboxes at once using a CSV file. This format is{" "}
            <strong>Instantly.ai compliant</strong> - you can use the same CSV format
            used for Instantly.ai bulk imports.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Step 1: Download Template
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={downloadTemplate}
            size="small"
          >
            Download CSV Template
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            The template includes example data showing the required format.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Step 2: Upload Your CSV File
          </Typography>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: "none" }}
            ref={fileInputRef}
          />
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current?.click()}
            size="small"
          >
            Select CSV File
          </Button>
        </Box>

        {parseError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {parseError}
          </Alert>
        )}

        {parsedInboxes.length > 0 && !importComplete && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview ({parsedInboxes.length} inbox{parsedInboxes.length !== 1 ? "es" : ""})
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>IMAP Host</TableCell>
                    <TableCell>SMTP Host</TableCell>
                    <TableCell>Warmup</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedInboxes.map((inbox, index) => (
                    <TableRow key={index}>
                      <TableCell>{inbox.email}</TableCell>
                      <TableCell>
                        {inbox.firstName} {inbox.lastName}
                      </TableCell>
                      <TableCell>{inbox.imapHost}</TableCell>
                      <TableCell>{inbox.smtpHost}</TableCell>
                      <TableCell>
                        {inbox.warmupEnabled?.toLowerCase() === "true" ? "Yes" : "No"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {importComplete && importResults.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Import Results
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importResults.map((result, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {result.success ? (
                          <CheckCircle color="primary" fontSize="small" />
                        ) : (
                          <ErrorIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>{result.email}</TableCell>
                      <TableCell>
                        {result.success ? "Imported successfully" : result.error}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {importComplete ? "Close" : "Cancel"}
        </Button>
        {!importComplete && (
          <LoadingButton
            onClick={handleImport}
            variant="contained"
            loading={loading}
            disabled={parsedInboxes.length === 0}
          >
            Import {parsedInboxes.length > 0 ? `${parsedInboxes.length} Inbox${parsedInboxes.length !== 1 ? "es" : ""}` : ""}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InboxImportDialog;
